import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Layout, Menu, Button, Dropdown, Space, Typography, theme } from "antd";
import {
  DashboardOutlined,
  ShoppingCartOutlined,
  FileTextOutlined,
  UserOutlined,
  ShopOutlined,
  AppstoreOutlined,
  BgColorsOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  CheckSquareOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  LogoutOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { useLogoutMutation } from "../redux/api/authApi";
import { useGetLastSyncQuery } from "../redux/api/zendeskSyncApi";
import "./AdminLayout.css";

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const AdminLayout = ({ children }) => {
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  const navigate = useNavigate();
  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();
  const [collapsed, setCollapsed] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { token: themeToken } = theme.useToken();
  
  // Get last sync time
  const { data: lastSyncData } = useGetLastSyncQuery();
  const lastSync = lastSyncData?.last_sync;

  const isActive = (path) => location.pathname.startsWith(path);

  // Menu items configuration
  const menuItems = [
    {
      key: "/dashboard",
      icon: <DashboardOutlined />,
      label: "Dashboard",
    },
    {
      key: "/orders",
      icon: <ShoppingCartOutlined />,
      label: "Orders",
    },
    {
      key: "/proposals",
      icon: <FileTextOutlined />,
      label: "Proposals",
    },
    {
      key: "/persons",
      icon: <UserOutlined />,
      label: "Persons",
    },
    {
      type: "divider",
    },
    {
      key: "business",
      label: "Business",
      type: "group",
    },
    {
      key: "/manufacturers",
      icon: <ShopOutlined />,
      label: "Manufacturers",
    },
    {
      key: "/products",
      icon: <AppstoreOutlined />,
      label: "Products",
    },
    {
      key: "/materials",
      icon: <BgColorsOutlined />,
      label: "Materials",
    },
    {
      key: "/routes",
      icon: <EnvironmentOutlined />,
      label: "Routes",
    },
    {
      type: "divider",
    },
    {
      key: "sales",
      label: "Sales",
      type: "group",
    },
    {
      key: "/daily-report",
      icon: <FileTextOutlined />,
      label: "Daily Report",
    },
    {
      key: "/deals",
      icon: <DollarOutlined />,
      label: "Deals",
    },
    {
      key: "/tasks",
      icon: <CheckSquareOutlined />,
      label: "Tasks",
    }
  ];

  // Handle menu item click
  const handleMenuClick = ({ key }) => {
    navigate(key);
  };

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  // Keyboard shortcut to toggle sidebar (Ctrl/Cmd + B)
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
        event.preventDefault();
        setCollapsed((c) => !c);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Scroll to top functionality
  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout().unwrap();
    } catch (error) {
      // Even if logout API fails, the authSlice will clear local state
      console.warn('Logout API call failed:', error);
    }
    // Always navigate to login after logout attempt
    navigate("/login");
  };

  // User menu dropdown items
  const userMenuItems = [
    {
      key: "settings",
      icon: <SettingOutlined />,
      label: <Link to="/settings">Settings</Link>,
    },
    {
      type: "divider",
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: isLoggingOut ? "Logging out..." : "Logout",
      onClick: handleLogout,
      disabled: isLoggingOut,
    },
  ];

  // Get selected menu key based on current path
  const selectedKeys = menuItems
    .filter(item => item.key && isActive(item.key))
    .map(item => item.key);

  // Format last sync time
  const formatLastSyncTime = () => {
    if (!lastSync || !lastSync.completed_at) {
      return 'Never synced';
    }
    
    const syncDate = new Date(lastSync.completed_at);
    const now = new Date();
    const diffMs = now - syncDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    // Format date and time
    const dateStr = syncDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: syncDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
    const timeStr = syncDate.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true
    });
    
    // Add relative time
    let relativeTime = '';
    if (diffMins < 1) {
      relativeTime = 'Just now';
    } else if (diffMins < 60) {
      relativeTime = `${diffMins}m ago`;
    } else if (diffHours < 24) {
      relativeTime = `${diffHours}h ago`;
    } else if (diffDays < 7) {
      relativeTime = `${diffDays}d ago`;
    } else {
      relativeTime = '';
    }
    
    return `${dateStr} ${timeStr}${relativeTime ? ` (${relativeTime})` : ''}`;
  };

  return (
    <Layout className="admin-layout-container" style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={250}
        collapsedWidth={60}
        theme="dark"
        className="sidebar-sticky"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          backgroundColor: themeToken.colorDarkBg || '#212121',
        }}
      >
        <div style={{ 
          padding: '16px', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          {!collapsed && (
            <Text strong style={{ color: '#fff', fontSize: '16px' }}>Menu</Text>
          )}
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={toggleSidebar}
            style={{
              fontSize: '16px',
              width: 32,
              height: 32,
              color: '#fff',
            }}
          />
        </div>
        <div style={{ position: 'relative', minHeight: 'calc(100vh - 65px)', paddingBottom: collapsed ? 0 : 80 }}>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={selectedKeys}
            items={menuItems}
            onClick={handleMenuClick}
            style={{ borderRight: 0 }}
          />
          {/* Last Sync Time - Bottom of Sidebar */}
          {!collapsed && (
            <div
              style={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                right: 16,
                padding: '8px 12px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '4px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Text
                style={{
                  fontSize: '11px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  display: 'block',
                  lineHeight: '1.4',
                }}
              >
                Last sync:
              </Text>
              <Text
                style={{
                  fontSize: '11px',
                  color: 'rgba(255, 255, 255, 0.6)',
                  display: 'block',
                  fontWeight: 500,
                  marginTop: '2px',
                }}
              >
                {formatLastSyncTime()}
              </Text>
            </div>
          )}
        </div>
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 60 : 250, transition: 'margin-left 0.3s' }}>
        <Header
          style={{
            padding: '0 24px',
            background: themeToken.colorHeader || themeToken.colorDarkBg,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid ${themeToken.colorBorder}`,
            color: themeToken.colorBtnText || '#fff',
          }}
        >
          <Space>
            <Text strong style={{ color: themeToken.colorBtnText || '#fff' }}>
              Welcome, {user?.first_name}
            </Text>
            <Text 
              type="secondary" 
              style={{ 
                fontSize: '12px',
                color: themeToken.colorLightGrey || 'rgba(255, 255, 255, 0.65)'
              }}
            >
              (Ctrl+B to toggle sidebar)
            </Text>
          </Space>
          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Button 
              type="text" 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                color: themeToken.colorBtnText || '#fff'
              }}
            >
              <UserOutlined />
              <span>{user?.first_name}</span>
            </Button>
          </Dropdown>
        </Header>
        <Content
          style={{
            padding: '24px',
            background: themeToken.colorBgBase,
            minHeight: 280,
          }}
        >
          {children}
        </Content>
      </Layout>
        
        {/* Scroll to Top Button */}
        {showScrollTop && (
        <Button
          type="primary"
          shape="circle"
          icon={<UpOutlined />}
            onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            width: 50,
            height: 50,
            fontSize: '18px',
            zIndex: 1000,
          }}
        />
        )}
    </Layout>
  );
};

export default AdminLayout;
